import { AdjustmentsHistoryRecord } from "@/lib/catalog"
import { analytics } from "@/telemetry/amplitude"
import { atom } from "jotai"
import { currentPhotoAtom } from "../photo/photos-state"
import {
	getCurrentPresetAdjustments,
	hasWaitBlock,
	queuePreviewPresetDebounced,
	shouldApplyAutoOnPreset,
} from "../processing/image-processing"
import { store } from "../store"
import { getEditor } from "@/utils/useEditor"
import { processingQueue } from "../processing/processing-queue"
import { currentAdjustmentsAtom } from "../store-history"
import { PresetAdjustmentsResult, currentPresetAtom } from "../store-presets"
import { PresetItem } from "@/store/presets/presets-service"
import { Adjustments } from "@/core/types"
import { photoLoadingStateAtom } from "./photo-loading"

export const currentHoverPreviewAtom = atom<PreviewablePreset | PreviewableHistory | null>(null)

export interface PreviewablePreset extends PresetItem {
	type: string
}

export interface PreviewableHistory extends AdjustmentsHistoryRecord {
	type: string
}

export const resetPreview = () => {
	const currentPhoto = store.get(currentPhotoAtom)
	const editor = getEditor()
	if (currentPhoto) {
		processingQueue.removeTask(currentPhoto.id, "calculate")
	}
	editor.setAdjustments(store.get(currentAdjustmentsAtom))
	analytics.trackEvent({ label: "reset_preview", type: "click" })
}

export const previewHistory = (history: PreviewableHistory) => {
	const currentPhoto = store.get(currentPhotoAtom)
	const photoLoadingState = store.get(photoLoadingStateAtom)

	const editor = getEditor()
	if (
		currentPhoto &&
		store.get(currentHoverPreviewAtom) === history &&
		photoLoadingState == "loaded"
	) {
		editor.setAdjustments(history.adjustments)
		analytics.trackEvent({ label: "preview_history", type: "click" })
	}
}

export const previewPreset = (preset: PresetItem, strength = 1) => {
	if (hasWaitBlock("adjustments")) {
		return
	}

	const editor = getEditor()
	const currentPhoto = store.get(currentPhotoAtom)
	const photoLoadingState = store.get(photoLoadingStateAtom)

	const matchesCurrentHoverPreset = (presetId: string) => {
		const currentPreview = store.get(currentHoverPreviewAtom)
		return (
			currentPreview &&
			currentPreview.type === "preset" &&
			(currentPreview as PreviewablePreset).id === presetId
		)
	}

	const matchesCurrentSelectedPreset = (presetId: string) => {
		return store.get(currentPresetAtom)?.preset?.id === presetId
	}

	if (
		currentPhoto &&
		(matchesCurrentHoverPreset(preset.id) ||
			(matchesCurrentSelectedPreset(preset.id) && photoLoadingState == "loaded"))
	) {
		const matchesCurrentPhotoState = () => {
			const latestPhoto = store.get(currentPhotoAtom)
			return (
				latestPhoto?.id === currentPhoto.id &&
				latestPhoto.adjustmentsDigest === currentPhoto.adjustmentsDigest
			)
		}

		const applyPresetResult = (result: PresetAdjustmentsResult) => {
			const isMatchingPreset =
				matchesCurrentHoverPreset(result.presetId) ||
				matchesCurrentSelectedPreset(result.presetId)

			if (isMatchingPreset && matchesCurrentPhotoState() && !hasWaitBlock("adjustments")) {
				const adjustments = shouldApplyAutoOnPreset()
					? (result.finalAdjustments as Adjustments)
					: result.presetAdjustments
				editor.setAdjustments(adjustments)
				analytics.trackEvent({
					label: "preview_preset",
					type: "click",
					value: preset.id,
				})
			}
		}

		const result = getCurrentPresetAdjustments(currentPhoto, preset, strength)

		if (result) {
			applyPresetResult(result)
		} else {
			queuePreviewPresetDebounced(currentPhoto, preset, strength, (result) => {
				if (currentPhoto.id === result.photoId && matchesCurrentPhotoState()) {
					applyPresetResult(result.presetResult)
				}
			})
		}
	}
}
